import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

import { Dialog } from '../../base/dialog';
import { getBrandingColorPalette } from '../../base/styles';
import { admitAllLobbyParticipants } from '../actions.any';
import { getLobbyAdmissionRooms } from '../functions';

declare var interfaceConfig: Object;

/**
 * Title i18n key per destination kind. Breakout rooms are named by the
 * moderator, so they carry their own label instead.
 */
const ROOM_TITLE_KEYS = {
    backstage: 'lobby.admissionBackstage',
    breakout: 'lobby.admissionBreakout',
    main: 'breakoutRooms.mainRoom'
};

/**
 * Explanatory line shown under each destination.
 */
const ROOM_HINT_KEYS = {
    backstage: 'lobby.admissionBackstageHint',
    breakout: 'lobby.admissionBreakoutHint',
    main: 'lobby.admissionMainHint'
};

const useStyles = makeStyles()(() => {
    const brand = getBrandingColorPalette(interfaceConfig);
    const primary = brand?.primary || '#4b6790';

    return {
        root: {
            color: 'inherit',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: "'Manrope', system-ui, sans-serif"
        },
        description: {
            fontSize: 14,
            lineHeight: '20px',
            margin: '4px 0 16px'
        },
        option: {
            alignItems: 'flex-start',
            backgroundColor: '#fff',
            border: '1px solid rgba(75, 103, 144, 0.25)',
            borderRadius: 8,
            color: 'inherit',
            cursor: 'pointer',
            display: 'flex',
            gap: 12,
            marginBottom: 12,
            padding: '14px 16px',
            transition: 'border-color 150ms ease, background-color 150ms ease, box-shadow 150ms ease',

            '&:hover': {
                borderColor: primary,
                backgroundColor: `${primary}0d`
            },

            '&:last-child': {
                marginBottom: 0
            }
        },
        optionSelected: {
            backgroundColor: `${primary}14`,
            borderColor: primary,
            boxShadow: `0 0 0 1px ${primary}`
        },
        radio: {
            accentColor: primary,
            cursor: 'pointer',
            flexShrink: 0,
            height: 18,
            marginTop: 2,
            width: 18
        },
        optionText: {
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0
        },
        optionTitle: {
            color: '#1f2937',
            fontSize: 14,
            fontWeight: 600,
            lineHeight: '20px'
        },
        optionHint: {
            color: '#6b7280',
            fontSize: 13,
            lineHeight: '18px',
            marginTop: 4
        }
    };
});

/**
 * The destination list is rebuilt on every call, so compare it by identity of
 * the rooms it describes - otherwise the dialog re-renders on every store
 * update and loses nothing but does so constantly.
 *
 * @param {Array<Object>} previous - Previously selected rooms.
 * @param {Array<Object>} next - Newly selected rooms.
 * @returns {boolean}
 */
function _sameRooms(previous, next) {
    return previous.length === next.length
        && previous.every((room, index) => room.key === next[index].key && room.name === next[index].name);
}

/**
 * Lets a moderator choose where admitted lobby guests should land: the meeting
 * itself, its backstage room, or one of its breakout rooms. Shown no matter
 * which of those rooms the moderator is admitting from.
 *
 * @param {Object} props - Component props.
 * @returns {JSX.Element}
 */
const LobbyAdmissionDialog = ({ onAdmissionComplete, participants }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { classes, cx } = useStyles();
    const rooms = useSelector(getLobbyAdmissionRooms, _sameRooms);
    const [ selectedKey, setSelectedKey ] = useState('main');

    // A breakout room can be removed while the dialog is open; fall back to the
    // meeting rather than submitting a destination that no longer exists.
    const selectedRoom = rooms.find(room => room.key === selectedKey) || rooms[0];

    const getRoomName = useCallback(room => room.name || t(ROOM_TITLE_KEYS[room.kind]), [ t ]);

    const onSubmit = useCallback(() => {
        if (!selectedRoom) {
            return false;
        }

        // The meeting is the default destination, so it travels as "no
        // destination" - that keeps the ordinary admit path byte-identical to
        // what it was before destinations existed.
        dispatch(admitAllLobbyParticipants(
            participants, selectedRoom.kind === 'main' ? undefined : selectedRoom.jid));
        onAdmissionComplete?.();

        return true;
    }, [ dispatch, onAdmissionComplete, participants, selectedRoom ]);

    return (
        <Dialog
            cancelKey = 'dialog.Cancel'
            okKey = 'lobby.admissionConfirm'
            onSubmit = { onSubmit }
            submitDisabled = { !selectedRoom }
            titleKey = 'lobby.admissionTitle'
            width = 'small'>
            <div
                className = { classes.root }
                id = 'lobby-admission-dialog'>
                <div className = { classes.description }>
                    { t('lobby.admissionDescription', { count: participants.length }) }
                </div>
                { rooms.map(room => {
                    const selected = selectedRoom?.key === room.key;

                    return (
                        <label
                            className = { cx(classes.option, selected && classes.optionSelected) }
                            key = { room.key }>
                            <input
                                checked = { selected }
                                className = { classes.radio }
                                name = 'lobby-admission-room'
                                onChange = { () => setSelectedKey(room.key) }
                                type = 'radio'
                                value = { room.key } />
                            <span className = { classes.optionText }>
                                <span className = { classes.optionTitle }>
                                    { getRoomName(room) }
                                </span>
                                <span className = { classes.optionHint }>
                                    { t(ROOM_HINT_KEYS[room.kind]) }
                                </span>
                            </span>
                        </label>
                    );
                }) }
            </div>
        </Dialog>
    );
};

export default LobbyAdmissionDialog;
